'use client';

import i18next, { type InitOptions, type Resource } from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { useEffect, useMemo } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

import { fallbackLng } from '@/features/common/i18n/settings';

const _i18nSource = process.env.NEXT_PUBLIC_I18N_SOURCE;

interface I18nProviderProps {
  children: React.ReactNode;
  lng: string;
  resources: Resource;
}

export default function I18nProvider({ children, lng, resources }: I18nProviderProps) {
  const i18n = useMemo(() => {
    const options: InitOptions = {
      lng,
      resources, // This prevents initial load on client
      fallbackLng,
      // debug: process.env.NODE_ENV === 'development',
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    };

    const instance = i18next.createInstance();
    instance
      .use(initReactI18next)
      // LanguageDetector is removed as it's browser-specific and causes SSR errors.
      // Language is passed directly via the `lng` prop.
      .use(HttpBackend)
      .init(options);

    return instance;
  }, [lng, resources]);

  useEffect(() => {
    const onLoaded = () => {
      console.log('New resources loaded on client, forcing re-render via language change event.');
      // This is an idiomatic way to force all `useTranslation` hooks to re-render.
      // It triggers the 'languageChanged' event which components are subscribed to.
      i18n.changeLanguage(lng);
    };

    i18n.on('loaded', onLoaded);

    return () => {
      i18n.off('loaded', onLoaded);
    };
  }, [i18n, lng]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
